import { useEffect, useMemo, useState } from 'react'
import { useDisplayUserError } from '@yes.technology/react-toolkit'
import {
  AvailableCredentials,
  CredentialItem,
  Credentials
} from 'authentication/shared/types/authentication.type'
import { useAuthentication } from 'authentication/shared/hooks'
import useUpgradeToken from 'authentication/shared/hooks/useUpgradeToken'
import { useQuery } from '@tanstack/react-query'
import { fetchCredentials } from 'authentication/shared/api'
import tryJwtDecode from 'shared/utils/jwt'

type Option = {
  id: string
  value: string
  label: string
}

const formatCredentialsOptions = (
  optionsUuids: CredentialItem[] | null | undefined,
  withSID: boolean
): Option[] => {
  if (!optionsUuids) {
    return []
  }

  return optionsUuids.map(({ uuid, sid, des }) => ({
    id: uuid,
    value: uuid,
    label: withSID ? `${sid} |  ${des}` : des
  }))
}

const getApplicationSystemOptions = (
  availableCredentials: AvailableCredentials
): Option[] => {
  return formatCredentialsOptions(
    availableCredentials.applicationsystems_info,
    false
  )
}

const getOrganizationOptions = (
  availableCredentials: AvailableCredentials,
  applicationSystemUuid: string
): Option[] => {
  const credentialList = availableCredentials?.credential_list
  const organizationsInfo = availableCredentials?.organizations_info?.filter(
    (organization) =>
      !credentialList ||
      credentialList.some(
        (credential) =>
          organization.uuid === credential.organization_uuid &&
          credential.applicationsystem_uuid === applicationSystemUuid
      )
  )
  return formatCredentialsOptions(organizationsInfo, true)
}

const getUserOptions = (
  availableCredentials: AvailableCredentials,
  applicationSystemUuid: string,
  organizationUuid: string
): Option[] => {
  const credentialList = availableCredentials?.credential_list
  const userInfo = availableCredentials.user_info?.filter(
    (user) =>
      !credentialList ||
      credentialList.some(
        (credential) =>
          user.uuid === credential.user_uuid &&
          credential.applicationsystem_uuid === applicationSystemUuid &&
          credential.organization_uuid === organizationUuid
      )
  )

  return formatCredentialsOptions(userInfo, true)
}

const useCredentials = () => {
  const { authenticatedData, startLoading, stopLoading } = useAuthentication()

  const upgradeToken = useUpgradeToken()

  const { displayUserError } = useDisplayUserError()

  const { token } = authenticatedData

  const { user_id: userUuid, credentials: tokenCredentials } =
    tryJwtDecode<{ user_id: string; credentials: Credentials }>(token || '') ||
    {}

  const { data: fetchedAvailableCredentials } = useQuery<
    AvailableCredentials | null,
    Error
  >({
    queryKey: ['filteraction', userUuid],
    queryFn: () => fetchCredentials(userUuid as string),
    enabled: !authenticatedData.available_credentials && !!userUuid
  })

  const [selectedCredentials, setSelectedCredentials] = useState<Credentials>({
    applicationsystem_uuid: tokenCredentials?.applicationsystem_uuid || '',
    organization_uuid: tokenCredentials?.organization_uuid || '',
    user_uuid: tokenCredentials?.user_uuid || ''
  })

  const credentials = useMemo(() => {
    const cachedAvailableCredentials = authenticatedData?.available_credentials
    const availableCredentials =
      cachedAvailableCredentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const applicationSystemOptions =
      getApplicationSystemOptions(availableCredentials)

    const organizationOptions = getOrganizationOptions(
      availableCredentials,
      selectedCredentials?.applicationsystem_uuid
    )

    const userOptions = getUserOptions(
      availableCredentials,
      selectedCredentials?.applicationsystem_uuid,
      selectedCredentials?.organization_uuid
    )

    return {
      applicationSystem: {
        options: applicationSystemOptions
      },
      organization: {
        options: organizationOptions
      },
      user: {
        options: userOptions
      }
    }
  }, [authenticatedData, fetchedAvailableCredentials, selectedCredentials])

  const submitCredentials = async (
    onSuccess: () => void,
    onError: (error?: unknown) => void
  ) => {
    startLoading()

    try {
      await upgradeToken(selectedCredentials)
      onSuccess?.()
    } catch (error) {
      onError?.(error)
      displayUserError({ error })
      console.error(error)
    } finally {
      stopLoading()
    }
  }

  const setEcosystem = (ecosystemUuid: string) => {
    setSelectedCredentials((prev) => ({
      ...prev,
      ecosystem_uuid: ecosystemUuid
    }))
  }

  const setApplicationSystem = (applicationSystemUuid: string) => {
    const availableCredentials =
      authenticatedData?.available_credentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const organizationOptions = getOrganizationOptions(
      availableCredentials,
      applicationSystemUuid
    )

    const isOrganizationSelectedValid = organizationOptions.some(
      (option) => option.id === selectedCredentials?.organization_uuid
    )

    const organizationUuid = isOrganizationSelectedValid
      ? selectedCredentials?.organization_uuid
      : organizationOptions?.[0]?.id

    const userOptions = getUserOptions(
      availableCredentials,
      applicationSystemUuid,
      organizationUuid
    )

    const isUserSelectedValid = userOptions.some(
      (option) => option.id === selectedCredentials?.user_uuid
    )

    const userUuid = isUserSelectedValid
      ? selectedCredentials?.user_uuid
      : userOptions?.[0]?.id

    setSelectedCredentials((prev) => ({
      ...prev,
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    }))
  }

  const setOrganization = (organizationUuid: string) => {
    const availableCredentials =
      authenticatedData?.available_credentials || fetchedAvailableCredentials

    if (!availableCredentials) return null

    const userOptions = getUserOptions(
      availableCredentials,
      selectedCredentials?.applicationsystem_uuid,
      organizationUuid
    )

    const isUserSelectedValid = userOptions.some(
      (option) => option.id === selectedCredentials?.user_uuid
    )

    const userUuid = isUserSelectedValid
      ? selectedCredentials?.user_uuid
      : userOptions?.[0]?.id

    setSelectedCredentials((prev) => ({
      ...prev,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    }))
  }

  const setUser = (userUuid: string) => {
    setSelectedCredentials((prev) => ({
      ...prev,
      user_uuid: userUuid
    }))
  }

  useEffect(() => {
    const allCredentialsSelected = Object.values(selectedCredentials).every(
      (value) => value
    )

    if (!credentials || allCredentialsSelected) return

    const applicationSystemUuid =
      selectedCredentials?.applicationsystem_uuid ||
      credentials.applicationSystem.options?.[0]?.id
    const organizationUuid =
      selectedCredentials?.organization_uuid ||
      credentials.organization.options?.[0]?.id
    const userUuid =
      selectedCredentials?.user_uuid || credentials.user.options?.[0]?.id

    setSelectedCredentials({
      applicationsystem_uuid: applicationSystemUuid,
      organization_uuid: organizationUuid,
      user_uuid: userUuid
    })
  }, [credentials, selectedCredentials])

  return {
    credentials,
    selectedCredentials,
    submitCredentials,
    setEcosystem,
    setApplicationSystem,
    setOrganization,
    setUser
  }
}

export default useCredentials
